// src/modules/app-views/leads/components/CardHeaderLeadsTags.jsx
// *******************************************************************************
// LEADS CARD HEADER TAGS
// *******************************************************************************
// Displays status and origin tags for a lead document.
// Also shows the lead's page URL as a globe icon on the far right.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tag, Tooltip } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CardHeaderLeadsTags = ({ data: dataProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const currentPage = useZustand?.('module', 'currentPage', false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const data = dataProp || currentPage?.data;
  const status = data?.status;
  const utmSource = data?.utm?.source;
  const pageUrl = data?.leadMeta?.pageUrl;

  const statusCfg = getStatusConfig(status);
  const originCfg = getOriginConfig(utmSource);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
      {/* Left: globe icon + URL literal — grows to fill available space */}
      {pageUrl && (
        <Tooltip title={pageUrl} placement="top">
          <span
            style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: 0.75, flex: 1, minWidth: 0 }}
            onClick={() => window.open(pageUrl, '_blank')}
          >
            <GlobalOutlined style={{ fontSize: 13, flexShrink: 0 }} />
            <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {pageUrl}
            </span>
          </span>
        </Tooltip>
      )}

      {/* Right: status + origin tags */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
        <Tooltip title={translateX('status')}>
          <Tag color={statusCfg.color} style={{ margin: 0 }}>
            {translateX(statusCfg.label)}
          </Tag>
        </Tooltip>

        <Tooltip title={translateX('origin')}>
          <Tag color={originCfg.color} style={{ margin: 0 }}>
            {originCfg.label}
          </Tag>
        </Tooltip>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeaderLeadsTags;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getStatusConfig(status) {
  const cfg = {
    queue:       { color: 'orange',     label: 'queue' },
    available:   { color: 'blue',       label: 'available' },
    pending:     { color: 'default',    label: 'pending' },
    processing:  { color: 'processing', label: 'processing' },
    assigned:    { color: 'cyan',       label: 'assigned' },
    delivered:   { color: 'geekblue',   label: 'delivered' },
    purchased:   { color: 'green',      label: 'purchased' },
    in_progress: { color: 'processing', label: 'in_progress' },
    converted:   { color: 'success',    label: 'converted' },
    discarded:   { color: 'error',      label: 'discarded' },
    lost:        { color: 'error',      label: 'lost' },
    no_contact:  { color: 'warning',    label: 'no_contact' },
  };
  return cfg[status] || { color: 'default', label: status || 'queue' };
}

function getOriginConfig(source) {
  const map = {
    google:    { color: 'blue',     label: 'Google' },
    facebook:  { color: 'geekblue', label: 'Facebook' },
    meta:      { color: 'geekblue', label: 'Meta' },
    instagram: { color: 'magenta',  label: 'Instagram' },
    youtube:   { color: 'red',      label: 'YouTube' },
    email:     { color: 'cyan',     label: 'Email' },
    organic:   { color: 'green',    label: 'Organic' },
  };
  if (source && map[source.toLowerCase()]) {
    return map[source.toLowerCase()];
  }
  return { color: 'default', label: translateX('direct_url') };
}
